import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import TopicOfMonth from "../components/TopicOfMonth";
import { ContentActionsMenu } from "../components/ContentActionsMenu";
import EditableText from "../components/EditableText";
import TaxonomyManager from "../components/TaxonomyManager";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { 
  Search, Plus, MessageCircle, Clock, User, Tag, MapPin, Filter, X, EyeOff
} from "lucide-react";

// Fallback used only when the live taxonomy fetch returns nothing
// (cold-cache + 500, or admin temporarily disables all categories).
// Canonical source is the founder-managed `discussion_categories` taxonomy.
const FALLBACK_DISCUSSION_TAGS = [
  "Clinical Practice", "Patient Care", "Infection Control", "Technology",
  "Career", "Education", "Business", "Wellness", "Research", "Equipment"
];

const DiscussionsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items: taxonomyTags, refetch: refetchTags } = useTaxonomy("discussion_categories");
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [selectedTag, setSelectedTag] = useState(searchParams.get("tag") || "");
  const [selectedProvince, setSelectedProvince] = useState(searchParams.get("province") || "");
  const [allTags, setAllTags] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [showNewDiscussion, setShowNewDiscussion] = useState(false);
  const [newDiscussion, setNewDiscussion] = useState({
    title: "",
    content: "",
    tags: [],
    province_tags: [],
    is_anonymous: false
  });
  const [creating, setCreating] = useState(false);

  // Live-bound tag list driven by the founder-managed taxonomy. Renames /
  // additions / soft-deletes propagate to the new-discussion form and the
  // filter chips on the next render with no page refresh.
  const DISCUSSION_TAGS = useMemo(() => {
    if (taxonomyTags && taxonomyTags.length > 0) {
      return taxonomyTags.map(it => it.label).filter(Boolean);
    }
    return FALLBACK_DISCUSSION_TAGS;
  }, [taxonomyTags]);

  useEffect(() => {
    fetchProvinces();
  }, []);

  // Refresh the "all tags" set (union of taxonomy + any free-form tags
  // already attached to existing discussions) whenever the taxonomy changes.
  useEffect(() => {
    const fetchUsedTags = async () => {
      try {
        const response = await axios.get(`${API}/discussions/tags/all`);
        setAllTags([...new Set([...DISCUSSION_TAGS, ...response.data.tags])]);
      } catch {
        setAllTags(DISCUSSION_TAGS);
      }
    };
    fetchUsedTags();
  }, [DISCUSSION_TAGS]);

  useEffect(() => {
    fetchDiscussions();
  }, [search, selectedTag, selectedProvince]);

  const fetchProvinces = async () => {
    try {
      const response = await axios.get(`${API}/provinces`);
      setProvinces(response.data);
    } catch (error) {
      console.error("Failed to fetch provinces");
    }
  };

  const fetchDiscussions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (selectedTag) params.append("tag", selectedTag);
      if (selectedProvince) params.append("province", selectedProvince);

      const response = await axios.get(`${API}/discussions?${params.toString()}`);
      setDiscussions(response.data);
    } catch (error) {
      toast.error("Failed to load discussions");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (selectedTag) params.set("tag", selectedTag);
    if (selectedProvince) params.set("province", selectedProvince);
    setSearchParams(params);
  };

  const handleCreateDiscussion = async (e) => {
    e.preventDefault();
    if (!newDiscussion.title.trim() || !newDiscussion.content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/discussions`, newDiscussion);
      if (response.data.message) {
        toast.success(response.data.message);
      } else {
        toast.success("Discussion created!");
      }
      setShowNewDiscussion(false);
      setNewDiscussion({ title: "", content: "", tags: [], province_tags: [], is_anonymous: false });
      if (response.data.discussion_id && response.data.status !== "pending") {
        navigate(`/discussions/${response.data.discussion_id}`);
      } else {
        fetchDiscussions();
      }
    } catch (error) {
      toast.error("Failed to create discussion");
    } finally {
      setCreating(false);
    }
  };

  const toggleTag = (tag) => {
    setNewDiscussion(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const toggleProvinceTag = (province) => {
    setNewDiscussion(prev => ({
      ...prev,
      province_tags: prev.province_tags.includes(province)
        ? prev.province_tags.filter(p => p !== province)
        : [...prev.province_tags, province]
    }));
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedTag("");
    setSelectedProvince("");
    setSearchParams({});
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return "Just now";
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="discussions-page">
        {/* Topic of the Month Banner */}
        <TopicOfMonth isAdmin={user?.is_admin} compact={true} />

        {/* Anonymous board disclaimer */}
        <div className="bg-[#F0ECF7]/70 border border-[#E5DFEC] rounded-xl p-3 mb-6 flex items-start gap-2" data-testid="discussions-disclaimer">
          <EyeOff className="w-4 h-4 text-[#6B4E8A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#6B4E8A] leading-relaxed">
            Posts on this board may be anonymous. DHDH does not endorse the opinions expressed by members. Content is moderated but individual posts do not represent the views of DHDH.
          </p>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] inline-flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <EditableText blockKey="discussions.title" fallback="Discussions" as="span" />
              <TaxonomyManager
                kind="discussion_categories"
                title="Discussion categories"
                description="Manage the categories members can tag discussions with."
                triggerLabel="Manage discussion categories"
                onChange={refetchTags}
              />
            </h1>
            <EditableText
              blockKey="discussions.subtitle"
              fallback="Share knowledge and connect with fellow dental hygienists"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          <Dialog open={showNewDiscussion} onOpenChange={setShowNewDiscussion}>
            <DialogTrigger asChild>
              <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="new-discussion-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Discussion
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Start a New Discussion
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateDiscussion} className="space-y-6 mt-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={newDiscussion.title}
                    onChange={(e) => setNewDiscussion({ ...newDiscussion, title: e.target.value })}
                    placeholder="What would you like to discuss?"
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    data-testid="discussion-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    value={newDiscussion.content}
                    onChange={(e) => setNewDiscussion({ ...newDiscussion, content: e.target.value })}
                    placeholder="Share your thoughts, questions, or experiences..."
                    className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A] min-h-[150px]"
                    data-testid="discussion-content-input"
                  />
                </div>
                <div>
                  <Label>Topic Tags</Label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="discussion-tag-picker">
                    {DISCUSSION_TAGS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleTag(tag)}
                        data-testid={`discussion-tag-${tag.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          newDiscussion.tags.includes(tag)
                            ? 'bg-[#6B4E8A] text-white'
                            : 'bg-[#F0ECF7] text-[#6B4E8A] hover:bg-[#E5DFEC]'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Province Tags (Optional)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {provinces.map((p) => (
                      <button
                        key={p.code}
                        type="button"
                        onClick={() => toggleProvinceTag(p.code)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          newDiscussion.province_tags.includes(p.code)
                            ? 'bg-[#8DB5A0] text-white'
                            : 'bg-[#F0ECF7] text-[#6B4E8A] hover:bg-[#F0ECF7]/70'
                        }`}
                      >
                        {p.code}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Anonymous Posting Toggle */}
                <div className="flex items-center justify-between p-4 bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl">
                  <div className="flex items-center gap-3">
                    <EyeOff className="w-5 h-5 text-[#64748B]" />
                    <div>
                      <p className="text-sm font-medium text-[#6B4E8A]">Post Anonymously</p>
                      <p className="text-xs text-[#64748B]">Your identity will be hidden. Requires admin approval.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={newDiscussion.is_anonymous}
                    onClick={() => setNewDiscussion(prev => ({ ...prev, is_anonymous: !prev.is_anonymous }))}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                      newDiscussion.is_anonymous ? 'bg-[#8DB5A0]' : 'bg-[#E5DFEC]'
                    }`}
                    data-testid="anonymous-toggle"
                  >
                    <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform ${
                      newDiscussion.is_anonymous ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
                {newDiscussion.is_anonymous && (
                  <p className="text-xs text-[#8DB5A0] bg-[#8DB5A0]/10 p-3 rounded-lg">
                    Anonymous posts are reviewed by an admin before becoming visible to the community. You will be notified once your post is approved.
                  </p>
                )}
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewDiscussion(false)}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"
                    disabled={creating}
                    data-testid="submit-discussion-btn"
                  >
                    {creating ? "Creating..." : "Create Discussion"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filters */}
        <div className="bg-white border border-[#E5DFEC] rounded-2xl p-4 mb-6">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search discussions..."
                className="pl-12 bg-[#FAF7F2] border-[#E5DFEC] focus:border-[#6B4E8A]"
                data-testid="search-discussions-input"
              />
            </div>
            <div className="flex gap-3">
              <Select value={selectedTag || "all"} onValueChange={(v) => setSelectedTag(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[160px] bg-[#FAF7F2] border-[#E5DFEC]">
                  <Tag className="w-4 h-4 mr-2 text-[#64748B]" />
                  <SelectValue placeholder="Topic" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Topics</SelectItem>
                  {allTags.map((tag) => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedProvince || "all"} onValueChange={(v) => setSelectedProvince(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[160px] bg-[#FAF7F2] border-[#E5DFEC]">
                  <MapPin className="w-4 h-4 mr-2 text-[#64748B]" />
                  <SelectValue placeholder="Province" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Provinces</SelectItem>
                  {provinces.map((p) => (
                    <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="submit" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-6">
                Search
              </Button>
            </div>
          </form>
          {(search || selectedTag || selectedProvince) && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E5DFEC]">
              <span className="text-sm text-[#64748B]">Active filters:</span>
              {search && (
                <span className="px-2 py-1 bg-[#F0ECF7] text-[#6B4E8A] text-xs rounded-full flex items-center gap-1">
                  "{search}"
                  <button onClick={() => setSearch("")}><X className="w-3 h-3" /></button>
                </span>
              )}
              {selectedTag && (
                <span className="px-2 py-1 bg-[#F0ECF7] text-[#6B4E8A] text-xs rounded-full flex items-center gap-1">
                  {selectedTag}
                  <button onClick={() => setSelectedTag("")}><X className="w-3 h-3" /></button>
                </span>
              )}
              {selectedProvince && (
                <span className="px-2 py-1 bg-[#F0ECF7] text-[#6B4E8A] text-xs rounded-full flex items-center gap-1">
                  {selectedProvince}
                  <button onClick={() => setSelectedProvince("")}><X className="w-3 h-3" /></button>
                </span>
              )}
              <button onClick={clearFilters} className="text-xs text-[#8DB5A0] hover:underline ml-2">
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Discussions List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 animate-pulse">
                <div className="h-6 bg-[#F0ECF7] rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-[#F0ECF7] rounded w-full mb-2"></div>
                <div className="h-4 bg-[#F0ECF7] rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : discussions.length > 0 ? (
          <div className="space-y-4">
            {discussions.map((discussion, idx) => (
              <motion.div
                key={discussion.discussion_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="relative"
              >
                <div className="absolute top-3 right-3 z-10">
                  <ContentActionsMenu
                    testIdPrefix={`discussion-${discussion.discussion_id}`}
                    canManage={discussion.can_manage}
                    reportType="discussion"
                    reportId={discussion.discussion_id}
                    revealType={discussion.is_anonymous ? "discussion" : undefined}
                    revealId={discussion.discussion_id}
                    onEdit={() => navigate(`/discussions/${discussion.discussion_id}?edit=1`)}
                    onDelete={async () => {
                      try {
                        await axios.delete(`${API}/discussions/${discussion.discussion_id}`);
                        toast.success("Discussion deleted");
                        fetchDiscussions();
                      } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete"); }
                    }}
                    deleteTitle="Delete this discussion?"
                    deleteDescription="This permanently removes the discussion and all of its replies. This cannot be undone."
                  />
                </div>
                <Link
                  to={`/discussions/${discussion.discussion_id}`}
                  className={`block border rounded-2xl p-6 card-hover ${
                    discussion.is_starred
                      ? "bg-gradient-to-br from-[#F0ECF7] to-white border-[#6B4E8A]/30"
                      : "bg-white border-[#E5DFEC]"
                  }`}
                  data-testid={`discussion-item-${discussion.discussion_id}`}
                >
                  {discussion.is_starred && (
                    <div className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 rounded-full bg-[#6B4E8A] text-white text-[10px] font-medium uppercase tracking-wider" data-testid="discussion-starred-badge">
                      ★ {discussion.starred_reason === "topic_of_month" ? "Topic of the Month" : "Starred"}
                    </div>
                  )}
                  <h3 className="text-lg font-medium text-[#6B4E8A] mb-2 pr-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {discussion.title}
                  </h3>
                  <p className="text-[#64748B] text-sm line-clamp-2 mb-4">
                    {discussion.content}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    {discussion.tags?.slice(0, 3).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-[#F0ECF7] text-[#6B4E8A] text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                    {discussion.province_tags?.map((p) => (
                      <span key={p} className="px-2 py-0.5 bg-[#F0ECF7] text-[#6B4E8A] text-xs rounded-full">
                        {p}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#E5DFEC]">
                    <div className="flex items-center gap-2 text-sm text-[#64748B]">
                      <User className="w-4 h-4" />
                      {discussion.author_name}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#64748B]">
                      <MessageCircle className="w-4 h-4" />
                      {discussion.replies_count} replies
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#64748B]">
                      <Clock className="w-4 h-4" />
                      {formatDate(discussion.created_at)}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
            <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No discussions found
            </h3>
            <p className="text-[#64748B] mb-4">
              {search || selectedTag || selectedProvince
                ? "Try adjusting your search filters"
                : "Be the first to start a discussion!"}
            </p>
            <Button
              onClick={() => setShowNewDiscussion(true)}
              className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Start a Discussion
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DiscussionsPage;
